import { pdf } from '@react-pdf/renderer';
import { Buffer as BufferPolyfill } from 'buffer';
import ArrivalPDF from '../components/pdf/ArrivalPDF';
import DeparturePDF from '../components/pdf/DeparturePDF';
import TransitPDF from '../components/pdf/TransitPDF';
import PaymentPDF from '../components/pdf/PaymentPDF';
import api from './api';

// Add Buffer polyfill for @react-pdf/renderer (browser-safe)
const g = (typeof window !== 'undefined' ? window : {});
if (typeof g.Buffer === 'undefined') {
  // Set Buffer only if missing
  // eslint-disable-next-line no-param-reassign
  g.Buffer = BufferPolyfill;
}

/**
 * Frontend PDF Service
 * Handles PDF generation and email sending using frontend-generated PDFs
 */
class FrontendPDFService {
  constructor() {
    this.pdfComponents = {
      arrival: ArrivalPDF,
      departure: DeparturePDF,
      transit: TransitPDF,
      payment: PaymentPDF
    };
  }

  /**
   * Get the appropriate PDF component for the service type
   * @param {string} serviceType - Type of service (arrival, departure, transit, payment)
   * @returns {React.Component} - The appropriate PDF component
   */
  getPDFComponent(serviceType) {
    const normalizedType = serviceType ? serviceType.toLowerCase() : 'arrival';
    
    if (this.pdfComponents[normalizedType]) {
      return this.pdfComponents[normalizedType];
    }
    
    // Default to arrival PDF if type not found
    console.warn(`Unknown service type: ${serviceType}, defaulting to arrival PDF`);
    return this.pdfComponents.arrival;
  }

  /**
   * Map payment/booking data to PDF component format
   * @param {Object} data - Raw payment/booking data
   * @returns {Object} - Mapped data for PDF components
   */
  mapDataForPDF(data) {
    
    
    // Validate required data
    if (!data || typeof data !== 'object') {
      console.error('Invalid data passed to mapDataForPDF:', data);
      throw new Error('Invalid data passed to mapDataForPDF');
    }
    
    // Handle data from database structure (NESTED FORMAT)
    const flightDetails = data.flightDetails || {};
    const serviceDetails = data.serviceDetails || {};
    const priceBreakdown = data.priceBreakdown || {};
    
    // Extract values from nested database structure with better fallbacks
    const clientName = data.clientName || 'Unknown Customer';
    const clientEmail = data.clientEmail || 'unknown@example.com';
    const amount = typeof data.amount === 'number' ? data.amount : (priceBreakdown.totalPrice || data.amount || 0);
    const serviceType = data.serviceType || 'ARRIVAL';
    const categoryLevel = serviceDetails.levelName || 'Standard';
    
    // Enhanced phone number extraction
    const clientPhone = data.phoneNumber || data.clientPhone || data.contactPersonPhone || 'TBD';
    
    
    // Generate dummy data for TBD fields
    const dummyGreeters = [
      { name: 'Sarah Johnson', contact: '+44 7700 900123' },
      { name: 'Michael Chen', contact: '+44 7700 900456' },
      { name: 'Emma Williams', contact: '+44 7700 900789' },
      { name: 'David Brown', contact: '+44 7700 900321' },
      { name: 'Lisa Davis', contact: '+44 7700 900654' }
    ];
    
    const dummyDrivers = [
      { name: 'James Wilson', reg: 'LHR-2024-AVS' },
      { name: 'Maria Garcia', reg: 'LHR-2024-BVS' },
      { name: 'Robert Taylor', reg: 'LHR-2024-CVS' },
      { name: 'Anna Martinez', reg: 'LHR-2024-DVS' },
      { name: 'Thomas Anderson', reg: 'LHR-2024-EVS' }
    ];
    
    // Select dummy data based on booking ID for consistency
    const greeterIndex = (data._id?.slice(-1) || '0').charCodeAt(0) % dummyGreeters.length;
    const driverIndex = (data._id?.slice(-2, -1) || '0').charCodeAt(0) % dummyDrivers.length;
    
    const selectedGreeter = dummyGreeters[greeterIndex];
    const selectedDriver = dummyDrivers[driverIndex];
    
    // Calculate add-on details for extra luggage
    const baggageCount = serviceDetails.additionalServices?.bags?.count || 1;
    const additionalFees = priceBreakdown.additionalFees || 0;
    const hasExtraLuggage = baggageCount > 1 && additionalFees > 0;
    
    let addOnDetails = 'None';
    if (hasExtraLuggage) {
      // Additional fees are VAT-inclusive, so calculate net amount
      const additionalFeesNet = additionalFees / 1.2;
      addOnDetails = `Extra Luggage (${baggageCount} bags) - £${additionalFeesNet.toFixed(2)}`;
    }
    
    // Helper to read either a plain value or an object with a .value field (REMOVED - using direct access)

    // Normalize feature list to array of strings
    const normalizedFeatures = Array.isArray(serviceDetails.features)
      ? serviceDetails.features.map(f => typeof f === 'string' ? f : (f?.feature ?? '')).filter(Boolean)
      : [];

    const result = {
      // Basic info
      _id: (typeof data._id === 'string' ? data._id : (data._id?.toString?.() || 'unknown')),
      clientName: clientName,
      clientEmail: clientEmail,
      amount: amount,
      currency: data.currency || 'GBP',
      serviceType: serviceType,
      status: data.status || 'pending',
      
      // Flight details (DATABASE NESTED FORMAT)
      flightNumber: flightDetails.flightNumber || 'TBD',
      from: flightDetails.from || 'TBD',
      to: flightDetails.to || 'TBD',
      departureDate: flightDetails.departureDate || 'TBD',
      arrivalDate: flightDetails.arrivalDate || 'TBD',
      departureTime: this.extractTimeFromDate(flightDetails.departureDate),
      arrivalTime: this.extractTimeFromDate(flightDetails.arrivalDate),
      // For TRANSIT service type, use departureTime as transitTime
      transitTime: this.extractTimeFromDate(flightDetails.departureDate),
      
      // Service details (mapped from nested structure)
      serviceLevel: serviceDetails.levelName || categoryLevel,
      categoryName: serviceDetails.categoryName || serviceType,
      features: normalizedFeatures,
      pricingSource: serviceDetails.pricingSource || 'Dynamic Pricing System',
      
      // Additional services
      additionalServices: serviceDetails.additionalServices || {},
      baggageCount: baggageCount,
      baggageRange: serviceDetails.additionalServices?.bags?.range || 'standard',
      
      // Passenger count (DATABASE FORMAT)
      adults: data.adults || 1,
      children: data.children || 0,
      infants: data.infants || 0,
      
      // Pricing (mapped from nested structure with proper calculations)
      basePrice: priceBreakdown.basePrice || data.amount,
      additionalFees: additionalFees,
      totalPrice: priceBreakdown.totalPrice || data.amount,
      
      // Contact details (DATABASE FORMAT)
      clientPhone: clientPhone,
      contactPersonName: data.contactPersonName || clientName,
      contactPersonPhone: clientPhone,
      
      // Service-specific details (with dummy data fallbacks)
      greeterName: data.greeterName || selectedGreeter?.name || 'TBD',
      greeterContact: data.greeterContact || data.greeterPhone || selectedGreeter?.contact || 'TBD',
      driverName: data.driverName || selectedDriver?.name || 'TBD',
      driverRegistration: data.driverRegistration || selectedDriver?.reg || 'TBD',
      
      // Additional fields
      title: data.title,
      notes: data.notes,
      remarks: data.remarks,
      addOn: addOnDetails,
      
      // Dates (DATABASE FORMAT) - Enhanced date extraction
      serviceDate: data.serviceDate || flightDetails.departureDate || flightDetails.arrivalDate || data.createdAt || 'TBD',
      createdAt: data.createdAt || new Date(),
      paidAt: data.paidAt,
      
      // Stripe info
      stripePaymentIntentId: data.stripePaymentIntentId || null,
      stripeSessionId: data.stripeSessionId || null
    };
    
    // Final validation - ensure no undefined values
    Object.keys(result).forEach(key => {
      if (result[key] === undefined) {
        result[key] = null;
      }
    });
    
    return result;
  }

  /**
   * Extract time from date string or return TBD if not available
   * @param {string} dateString - Date string that might contain time
   * @returns {string} - Time in HH:MM format or 'TBD'
   */
  extractTimeFromDate(dateString) {
    if (!dateString || dateString === 'TBD') {
      return 'TBD';
    }
    
    try {
      // Handle ISO date strings like "2025-12-06T09:40:00Z"
      if (dateString.includes('T')) {
        const date = new Date(dateString);
        return date.toLocaleTimeString('en-GB', { 
          hour: '2-digit', 
          minute: '2-digit',
          hour12: false 
        });
      }
      
      // Handle other time formats
      if (dateString.includes(':')) {
        return dateString;
      }
      
      return 'TBD';
    } catch (error) {
      console.warn('Error extracting time from date:', dateString, error);
      return 'TBD';
    }
  }

  /**
   * Generate PDF blob from booking data
   * @param {Object} bookingData - Booking/payment data
   * @param {string} serviceType - Type of service
   * @returns {Promise<Blob>} - PDF blob
   */
  async generatePDFBlob(bookingData, serviceType) {
    try {
      // Re-fetch the complete payment request data (same as downloadPDF)
      let completeData = bookingData;
      if (bookingData._id) {
        try {
          const { paymentAPI } = await import('../services/api');
          const response = await paymentAPI.getPaymentRequestPublic(bookingData._id);
          if (response.success) {
            completeData = response.data;
          }
        } catch (error) {
          console.warn('Error fetching complete data for PDF blob:', error.message);
        }
      }
      
      const PDFComponent = this.getPDFComponent(serviceType);
      const mappedData = this.mapDataForPDF(completeData);
      
      // Validate mappedData before rendering
      if (!mappedData || typeof mappedData !== 'object') {
        throw new Error('Invalid mappedData for PDF rendering');
      }
      
      // Ensure all required fields are present and valid
      const requiredFields = ['clientName', 'clientEmail', 'amount', 'serviceType', 'serviceLevel'];
      for (const field of requiredFields) {
        if (!mappedData[field]) {
          mappedData[field] = field === 'amount' ? 0 : 'Unknown';
        }
      }
      
      // Add a small delay to prevent race conditions
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // Try to render the PDF component with error boundary
      let blob;
      try {
        blob = await pdf(<PDFComponent booking={mappedData} />).toBlob();
      } catch (renderError) {
        console.error('PDF Component render error:', renderError.message);
        
        // Try with minimal data to see if it's a data issue
        const minimalData = {
          clientName: mappedData.clientName || 'Test Customer',
          clientEmail: mappedData.clientEmail || 'test@example.com',
          amount: mappedData.amount || 0,
          serviceType: mappedData.serviceType || 'ARRIVAL',
          serviceLevel: mappedData.serviceLevel || 'Standard',
          currency: 'GBP',
          status: 'paid'
        };
        
        blob = await pdf(<PDFComponent booking={minimalData} />).toBlob();
      }
      
      return blob;
    } catch (error) {
      console.error('Error generating PDF blob:', error.message);
      throw new Error(`PDF generation failed: ${error.message}`);
    }
  }

  /**
   * Convert blob to base64 string
   * @param {Blob} blob - Blob to convert
   * @returns {Promise<string>} - Base64 string
   */
  async blobToBase64(blob) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const base64 = reader.result.split(',')[1]; // Remove data:application/pdf;base64, prefix
        resolve(base64);
      };
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  }

  /**
   * Generate PDF and convert to base64
   * @param {Object} bookingData - Booking/payment data
   * @param {string} serviceType - Type of service
   * @returns {Promise<string>} - Base64 PDF string
   */
  async generatePDFBase64(bookingData, serviceType) {
    try {
      const blob = await this.generatePDFBlob(bookingData, serviceType);
      const base64 = await this.blobToBase64(blob);
      return base64;
    } catch (error) {
      console.error('Error generating PDF base64:', error);
      throw new Error(`PDF generation failed: ${error.message}`);
    }
  }

  /**
   * Download PDF directly in browser
   * @param {Object} bookingData - Booking/payment data
   * @param {string} serviceType - Type of service
   * @param {string} filename - Optional filename
   * @returns {Promise<void>}
   */
  async downloadPDF(bookingData, serviceType, filename = null) {
    try {
      // Re-fetch the complete payment request data with authentication
      let completeData = bookingData;
      if (bookingData._id) {
        try {
          const { paymentAPI } = await import('../services/api');
          const response = await paymentAPI.getPaymentRequestPublic(bookingData._id);
          if (response.success) {
            completeData = response.data;
          }
        } catch (error) {
          console.warn('Error fetching complete data:', error.message);
        }
      }
      
      const blob = await this.generatePDFBlob(completeData, serviceType);
      
      // Generate filename if not provided
      if (!filename) {
        const referenceNumber = this.generateReferenceNumber(bookingData._id || 'booking', serviceType);
        const date = new Date().toISOString().split('T')[0];
        filename = `${serviceType}-${referenceNumber}-${date}.pdf`;
      }
      
      // Create download link
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      console.log(`PDF downloaded: ${filename}`);
    } catch (error) {
      console.error('Error downloading PDF:', error);
      throw new Error(`PDF download failed: ${error.message}`);
    }
  }

  /**
   * Send PDF via email using frontend generation
   * @param {Object} bookingData - Booking/payment data
   * @param {string} serviceType - Type of service
   * @param {string} recipientEmail - Recipient email address
   * @param {Object} options - Additional options
   * @returns {Promise<Object>} - Email sending result
   */
  async sendPDFViaEmail(bookingData, serviceType, recipientEmail, options = {}) {
    try {
      console.log(`📧 Generating PDF for ${serviceType} service...`);
      
      // Generate PDF as base64
      const pdfBase64 = await this.generatePDFBase64(bookingData, serviceType);
      
      console.log(`📧 Sending PDF via email to ${recipientEmail}...`);
      
      // Send to backend for email delivery
      const response = await api.post('email/send-frontend-pdf', {
        bookingData,
        serviceType,
        recipientEmail,
        pdfBase64,
        ...options
      });
      
      console.log('✅ PDF sent via email successfully');
      return response.data;
    } catch (error) {
      console.error('❌ Error sending PDF via email:', error);
      throw new Error(`Email sending failed: ${error.message}`);
    }
  }

  /**
   * Send booking confirmation email with PDF
   * @param {Object} bookingData - Booking data
   * @param {string} recipientEmail - Recipient email address
   * @param {string} serviceType - Type of service
   * @returns {Promise<Object>} - Email sending result
   */
  async sendBookingConfirmationEmail(bookingData, recipientEmail, serviceType = 'arrival', paymentRequestId = null) {
    return this.sendPDFViaEmail(bookingData, serviceType, recipientEmail, {
      emailType: 'booking_confirmation',
      subject: `Booking Confirmation - ${this.generateReferenceNumber(bookingData._id || 'booking', serviceType)}`,
      paymentRequestId
    });
  }

  /**
   * Send payment confirmation email with PDF
   * @param {Object} paymentData - Payment data
   * @param {string} recipientEmail - Recipient email address
   * @param {string} paymentRequestId - Payment request ID for database update
   * @returns {Promise<Object>} - Email sending result
   */
  async sendPaymentConfirmationEmail(paymentData, recipientEmail, paymentRequestId = null) {
    return this.sendPDFViaEmail(paymentData, 'payment', recipientEmail, {
      emailType: 'payment_confirmation',
      subject: `Payment Confirmation - ${paymentData.stripePaymentIntentId || 'PAY-000001'}`,
      paymentRequestId
    });
  }

  /**
   * Generate reference number
   * @param {string} id - Booking/payment ID
   * @param {string} serviceType - Service type
   * @returns {string} - Reference number
   */
  generateReferenceNumber(id, serviceType) {
    const prefix = serviceType.toUpperCase().substring(0, 3);
    const idString = id.toString();
    const shortId = idString.substring(idString.length - 6);
    return `${prefix}-${shortId}`;
  }

  /**
   * Get PDF preview URL
   * @param {Object} bookingData - Booking/payment data
   * @param {string} serviceType - Type of service
   * @returns {Promise<string>} - Preview URL
   */
  async getPDFPreviewURL(bookingData, serviceType) {
    try {
      const blob = await this.generatePDFBlob(bookingData, serviceType);
      const url = URL.createObjectURL(blob);
      return url;
    } catch (error) {
      console.error('Error generating PDF preview URL:', error);
      throw new Error(`PDF preview generation failed: ${error.message}`);
    }
  }

  /**
   * Clean up preview URL
   * @param {string} url - URL to revoke
   */
  revokePreviewURL(url) {
    if (url) {
      URL.revokeObjectURL(url);
    }
  }

  /**
   * Get supported service types
   * @returns {Array<string>} - Array of supported service types
   */
  getSupportedServiceTypes() {
    return Object.keys(this.pdfComponents);
  }

  /**
   * Validate service type
   * @param {string} serviceType - Service type to validate
   * @returns {boolean} - Whether service type is supported
   */
  isValidServiceType(serviceType) {
    return this.getSupportedServiceTypes().includes(serviceType.toLowerCase());
  }

}

// Create singleton instance
const frontendPDFService = new FrontendPDFService();

export default frontendPDFService;
